import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, LogIn, Menu, X, ArrowRight } from 'lucide-react';
import QualificationFormModal from '@/components/QualificationForm';
import { useSiteLang, TRANSLATED_PATHS, FlagES, FlagEN } from '@/components/site/SiteLang';

/*
  Cabecera única de la web pública (decisión de Samuel, 09/10/2026: el menú es FIJO en toda la web;
  ninguna sección abre «otra web» con otro menú). Vive en PublicLayout y no se vuelve a montar al
  cambiar de página: la navegación es interna (sin recargar).

  - Fija arriba. Transparente sobre los héroes oscuros a pantalla completa (OVERLAY_PATHS) hasta hacer
    scroll; en el resto, sólida desde el principio.
  - Escritorio (≥ 1024 px): menú con desplegable de Membresías, «Iniciar sesión» y CTA del test de idoneidad.
  - Móvil y tableta: botón «Acceder» siempre visible y menú a pantalla completa.
*/

const OVERLAY_PATHS = ['/', '/como-funciona', '/bootcamp-tokenizacion'];

const LABELS = {
  es: {
    how: 'Cómo funciona', memberships: 'Membresías', bootcamp: 'Bootcamp', events: 'Eventos', contact: 'Contacto',
    login: 'Iniciar sesión', access: 'Acceder', cta: 'Test de idoneidad', ctaLong: 'Hacer el test de idoneidad', menu: 'Menú', close: 'Cerrar menú',
    allMemberships: 'Todas las membresías', allDesc: 'Access, PRO y Premium, lado a lado',
    proDesc: 'Acceso completo al club y a la academia', premiumDesc: 'Todo lo de PRO más el Bootcamp y la certificación',
    compare: 'Comparativa', compareDesc: 'Qué incluye cada nivel',
  },
  en: {
    how: 'How it works', memberships: 'Memberships', bootcamp: 'Bootcamp', events: 'Events', contact: 'Contact',
    login: 'Sign in', access: 'Log in', cta: 'Suitability test', ctaLong: 'Take the suitability test', menu: 'Menu', close: 'Close menu',
    allMemberships: 'All memberships', allDesc: 'Access, PRO and Premium side by side',
    proDesc: 'Full access to the club and the academy', premiumDesc: 'Everything in PRO plus the Bootcamp and certification',
    compare: 'Comparison', compareDesc: 'What each tier includes',
  },
};

function membershipItems(L) {
  return [
    { to: '/membresias', label: L.allMemberships, desc: L.allDesc, exact: true },
    { to: '/membresias/pro', label: 'PRO', desc: L.proDesc },
    { to: '/membresias/premium', label: 'Premium', desc: L.premiumDesc },
    { to: '/membresias#comparativa', label: L.compare, desc: L.compareDesc },
  ];
}

export default function MainNavbar() {
  const { pathname } = useLocation();
  const { lang, setLang } = useSiteLang();
  const translated = TRANSLATED_PATHS.includes(pathname);
  const L = LABELS[translated ? lang : 'es'];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const dropRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Al cambiar de página: cierra menús
  useEffect(() => { setMobileOpen(false); setDropOpen(false); }, [pathname]);

  // Menú móvil: Esc cierra y el fondo no se desplaza
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [mobileOpen]);

  // Desplegable: Esc y clic fuera
  useEffect(() => {
    if (!dropOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDropOpen(false); };
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onClick); };
  }, [dropOpen]);

  const overlay = OVERLAY_PATHS.includes(pathname) && !scrolled && !mobileOpen;
  const inMemberships = pathname.startsWith('/membresias');
  const items = membershipItems(L);

  const linkCls = ({ isActive }) =>
    `relative text-[14px] tracking-wide transition-colors py-2 ${isActive ? 'text-white' : 'text-white/70 hover:text-white'} ` +
    `after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-[#c9a84c] after:transition-transform after:origin-left ${isActive ? 'after:scale-x-100' : 'after:scale-x-0 hover:after:scale-x-100'}`;

  const openDrop = () => { clearTimeout(closeTimer.current); setDropOpen(true); };
  const closeDropSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setDropOpen(false), 150); };

  const LangSwitch = ({ size = 'w-7 h-7' }) => (
    <div className="flex items-center" data-testid="lang-switcher">
      {[['es', FlagES, 'Español'], ['en', FlagEN, 'English']].map(([code, Flag, label]) => (
        <button key={code} type="button" onClick={() => setLang(code)} data-testid={`lang-${code}`} aria-label={label} aria-pressed={lang === code}
          className={`w-10 h-10 flex items-center justify-center transition-opacity ${lang === code ? '' : 'opacity-50 hover:opacity-80'}`}>
          <span className={`${size} rounded-full overflow-hidden border-2 flex items-center justify-center ${lang === code ? 'border-[#c9a84c]' : 'border-transparent'}`}>
            <Flag className={size} />
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 ${mobileOpen ? 'z-[10000]' : 'z-50'} transition-[background-color,box-shadow,border-color] duration-300 border-b ${
          overlay ? 'bg-transparent border-transparent' : 'bg-[#0a1628]/95 backdrop-blur-md border-white/[0.06] shadow-lg shadow-black/20'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 gap-4">
            <Link to="/" className="flex items-center shrink-0" aria-label="Freedom Homes Club, inicio">
              <img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-[68px] lg:h-[76px] w-auto" />
            </Link>

            {/* Menú de escritorio */}
            <nav className="hidden lg:flex items-center gap-7" aria-label="Principal">
              <NavLink to="/como-funciona" className={linkCls} data-testid="nav-holding">{L.how}</NavLink>
              <div className="relative" ref={dropRef} onMouseEnter={openDrop} onMouseLeave={closeDropSoon}>
                <button type="button" onClick={() => setDropOpen((o) => !o)} aria-expanded={dropOpen} aria-haspopup="true"
                  className={`relative flex items-center gap-1 text-[14px] tracking-wide py-2 transition-colors ${inMemberships ? 'text-white' : 'text-white/70 hover:text-white'}`}
                  data-testid="nav-membresias">
                  {L.memberships}
                  <ChevronDown size={14} className={`transition-transform ${dropOpen ? 'rotate-180' : ''}`} />
                  {inMemberships && <span className="absolute left-0 right-5 -bottom-0.5 h-px bg-[#c9a84c]" />}
                </button>
                {dropOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid="nav-membresias-menu">
                    <div className="w-[340px] rounded-2xl border border-white/[0.08] bg-[#0a1628] shadow-2xl shadow-black/40 p-2">
                      {items.map((it) => (
                        <Link key={it.to} to={it.to} onClick={() => setDropOpen(false)}
                          className="group flex items-start justify-between gap-3 rounded-xl px-4 py-3 hover:bg-white/[0.04] transition-colors">
                          <span>
                            <span className="block text-sm font-medium text-white">{it.label}</span>
                            <span className="block text-xs text-white/50 mt-0.5">{it.desc}</span>
                          </span>
                          <ArrowRight size={14} className="mt-1 text-[#c9a84c] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <NavLink to="/bootcamp-tokenizacion" className={linkCls} data-testid="nav-bootcamp">{L.bootcamp}</NavLink>
              <NavLink to="/eventos" className={linkCls} data-testid="nav-eventos">{L.events}</NavLink>
              <NavLink to="/contacto" className={linkCls} data-testid="nav-contacto">{L.contact}</NavLink>
            </nav>

            <div className="flex items-center gap-2 lg:gap-4">
              {translated && <div className="hidden sm:block"><LangSwitch /></div>}
              <Link to="/dashboard/login" className="hidden lg:flex items-center gap-1.5 text-[14px] text-[#c9a84c] hover:text-[#d8b85c] transition-colors" data-testid="nav-login">
                <LogIn size={14} /> {L.login}
              </Link>
              <button type="button" onClick={() => setModalOpen(true)} data-testid="nav-cta"
                className="hidden lg:inline-flex items-center h-11 px-5 text-sm font-semibold bg-[#c9a84c] text-[#0a1628] hover:bg-[#d8b85c] transition-colors">
                {L.cta}
              </button>
              <Link to="/dashboard/login" className="lg:hidden flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-semibold text-[#0a1628] bg-[#c9a84c] hover:bg-[#d8b85c] transition-colors" data-testid="nav-mobile-login">
                <LogIn size={14} /> {L.access}
              </Link>
              <button type="button" onClick={() => setMobileOpen((o) => !o)} aria-expanded={mobileOpen} aria-controls="site-mobile-menu"
                aria-label={mobileOpen ? L.close : L.menu}
                className="lg:hidden w-11 h-11 -mr-2 flex items-center justify-center text-white/80 hover:text-white" data-testid="nav-mobile-toggle">
                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Menú móvil y tableta: pantalla completa bajo la cabecera */}
      {mobileOpen && (
        <div id="site-mobile-menu" className="lg:hidden fixed inset-x-0 top-20 bottom-0 z-[9999] bg-[#0a1628] overflow-y-auto" data-testid="nav-mobile-menu">
          <nav className="max-w-xl mx-auto px-6 py-6 flex flex-col" aria-label="Principal">
            {[['/como-funciona', L.how]].map(([to, label]) => (
              <NavLink key={to} to={to} className={({ isActive }) => `min-h-[52px] flex items-center justify-between border-b border-white/[0.06] text-lg ${isActive ? 'text-[#c9a84c]' : 'text-white'}`}>
                {label}<ArrowRight size={16} className="text-white/30" />
              </NavLink>
            ))}
            <div className="border-b border-white/[0.06] py-3">
              <p className={`text-lg min-h-[40px] flex items-center ${inMemberships ? 'text-[#c9a84c]' : 'text-white'}`}>{L.memberships}</p>
              <div className="flex flex-col pl-3 border-l border-[#c9a84c]/30 ml-1">
                {items.map((it) => (
                  <Link key={it.to} to={it.to} onClick={() => setMobileOpen(false)} className="min-h-[44px] flex items-center text-[15px] text-white/70 hover:text-white">
                    {it.label}
                  </Link>
                ))}
              </div>
            </div>
            {[['/bootcamp-tokenizacion', L.bootcamp], ['/eventos', L.events], ['/contacto', L.contact]].map(([to, label]) => (
              <NavLink key={to} to={to} className={({ isActive }) => `min-h-[52px] flex items-center justify-between border-b border-white/[0.06] text-lg ${isActive ? 'text-[#c9a84c]' : 'text-white'}`}>
                {label}<ArrowRight size={16} className="text-white/30" />
              </NavLink>
            ))}
            <div className="mt-8 flex flex-col gap-3">
              <button type="button" onClick={() => { setMobileOpen(false); setModalOpen(true); }}
                className="h-12 w-full bg-[#c9a84c] text-[#0a1628] font-semibold text-sm">{L.ctaLong}</button>
              <Link to="/dashboard/login" className="h-12 w-full flex items-center justify-center gap-2 border border-white/15 text-white text-sm">
                <LogIn size={15} /> {L.login}
              </Link>
              {translated && <div className="flex justify-center pt-2"><LangSwitch size="w-8 h-8" /></div>}
            </div>
          </nav>
        </div>
      )}

      <QualificationFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source={`navbar${pathname.replace(/\//g, '-')}`} lang={translated ? lang : 'es'} />
    </>
  );
}
